import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PublicLayout } from '../../components/PublicLayout';
import { guestsAPI } from '../../lib/api';
import countries from '../../lib/countries';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Loader2,
  CheckCircle,
  ClipboardList,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';

export default function GuestRegister() {
  const { slug } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    id_passport: '',
    address: '',
    date_of_birth: '',
    nationality: '',
    purpose_of_visit: '',
    emergency_contact: '',
    vehicle_details: '',
    company_name: '',
  });

  useEffect(() => {
    guestsAPI
      .getPropertyInfo(slug)
      .then((res) => setProperty(res.data))
      .catch(() => setProperty(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await guestsAPI.register(slug, { ...form, property_id: property.id });
      setSuccess(true);
      toast.success('Registration submitted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PublicLayout>
    );
  }

  if (!property) {
    return (
      <PublicLayout>
        <div className="text-center py-20">
          <h2 className="text-xl font-bold">Property not found</h2>
          <p className="text-muted-foreground mt-2">This registration link is invalid.</p>
        </div>
      </PublicLayout>
    );
  }

  if (success) {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto py-16 text-center" data-testid="registration-success">
          <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
          <h2 className="text-2xl font-bold mb-2">Registration Complete</h2>
          <p className="text-muted-foreground mb-1">
            Thank you, <strong>{form.name}</strong>!
          </p>
          <p className="text-muted-foreground">
            Your details have been submitted to <strong>{property.name}</strong>.
          </p>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto py-8 px-4" data-testid="guest-register-page">
        <Card className="shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              <CardTitle className="text-xl">Guest Registration</CardTitle>
            </div>
            <p className="text-sm text-muted-foreground">
              {property.name} — {property.city}, {property.country}
            </p>
          </CardHeader>

          <Separator />

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Personal Details */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Personal Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Full name as on ID/Passport"
                      required
                      data-testid="reg-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      data-testid="reg-email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+1 234 567 890"
                      required
                      data-testid="reg-phone"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="date_of_birth">Date of Birth *</Label>
                    <Input
                      id="date_of_birth"
                      name="date_of_birth"
                      type="date"
                      value={form.date_of_birth}
                      onChange={handleChange}
                      required
                      data-testid="reg-dob"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="nationality">Nationality *</Label>
                    <Select
                      value={form.nationality}
                      onValueChange={(v) => setForm((prev) => ({ ...prev, nationality: v }))}
                    >
                      <SelectTrigger data-testid="reg-nationality">
                        <SelectValue placeholder="Select nationality" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {countries.map((c) => (
                          <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* ID & Address */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">ID & Address</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="id_passport">ID / Passport Number *</Label>
                    <Input
                      id="id_passport"
                      name="id_passport"
                      value={form.id_passport}
                      onChange={handleChange}
                      placeholder="Passport or national ID number"
                      required
                      data-testid="reg-id"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="country">Country *</Label>
                    <Select
                      value={form.country}
                      onValueChange={(v) => setForm((prev) => ({ ...prev, country: v }))}
                    >
                      <SelectTrigger data-testid="reg-country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {countries.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="address">Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                      placeholder="Full residential address"
                      required
                      data-testid="reg-address"
                    />
                  </div>
                </div>
              </div>

              {/* Optional Details */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Additional Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="purpose_of_visit">Purpose of Visit</Label>
                    <Input
                      id="purpose_of_visit"
                      name="purpose_of_visit"
                      value={form.purpose_of_visit}
                      onChange={handleChange}
                      placeholder="Business, Leisure, etc."
                      data-testid="reg-purpose"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="emergency_contact">Emergency Contact</Label>
                    <Input
                      id="emergency_contact"
                      name="emergency_contact"
                      value={form.emergency_contact}
                      onChange={handleChange}
                      placeholder="Name & phone number"
                      data-testid="reg-emergency"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="vehicle_details">Vehicle Details</Label>
                    <Input
                      id="vehicle_details"
                      name="vehicle_details"
                      value={form.vehicle_details}
                      onChange={handleChange}
                      placeholder="License plate, model"
                      data-testid="reg-vehicle"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={form.company_name}
                      onChange={handleChange}
                      placeholder="If applicable"
                      data-testid="reg-company"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Button type="submit" className="w-full" disabled={submitting} data-testid="reg-submit">
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Globe className="h-4 w-4 mr-2" />
                  )}
                  Submit Registration
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}
